import { StateCreator } from "zustand";
import {
  ICPEmployeeCountDistribution,
  ICPGeographyDistribution,
  ICPIndustryDistribution,
  ICPRevenueDistribution,
} from "../../../apis/companySearch/icpDistribution";
import { CompanyMatchFilter } from "../../../legacy/types/companyFilter";
import { ICPAnalysisMethod } from "../../../pages/ICP/types";
import { SAMConfig, SOMConfig, ICPConfig } from "../../../types/workspace";
import { WorkspaceState } from "../WorkspaceStore";
import { GraphQLClient } from "../../../utils/apollo";
import { COMPANY_MATCH_FILE_STATS } from "../../../apis/companyMatch/queries";
import {
  CompanyMatchStatusPayload,
  CompanyMatchUploadResponse,
} from "../../../types/companyMatch";
import { COMPANY_MATCH_CLIENT } from "../../../utils/consts";

export interface ICPFileUpload {
  companyMatch?: CompanyMatchFilter;
}

export interface WorkspaceICPSlice {
  icpSelectedProducts: number[];
  icpSAM?: Partial<SAMConfig>;
  icpSOM?: Partial<SOMConfig>;
  icpAnalysisMethod: ICPAnalysisMethod;
  icpCustomerListData: ICPFileUpload | undefined;
  isSamIcpAppliedToConfig: boolean;
  isSomIcpAppliedToConfig: boolean;
  isNewIcpAvailableForSam: boolean;
  isNewIcpAvailableForSom: boolean;
  isLoadingIcpData: boolean;

  // Distribution data
  icpIndustryDistribution: ICPIndustryDistribution[];
  icpGeographyDistribution: ICPGeographyDistribution[];
  icpRevenueDistribution: ICPRevenueDistribution[];
  icpEmployeeCountDistribution: ICPEmployeeCountDistribution[];

  setICPSelectedProducts: (productIds: number[]) => void;
  setIcpSAM: (sam: Partial<SAMConfig>) => void;
  setIcpSOM: (som: Partial<SOMConfig>) => void;
  setICPAnalysisMethod: (method: ICPAnalysisMethod) => void;
  setICPCustomerListData: (data: ICPFileUpload | undefined) => void;

  // Distribution data setters
  setICPIndustryDistribution: (data: ICPIndustryDistribution[]) => void;
  setICPGeographyDistribution: (data: ICPGeographyDistribution[]) => void;
  setICPRevenueDistribution: (data: ICPRevenueDistribution[]) => void;
  setICPEmployeeCountDistribution: (
    data: ICPEmployeeCountDistribution[]
  ) => void;

  setIsSamIcpAppliedToConfig: (isApplied: boolean) => void;
  setIsSomIcpAppliedToConfig: (isApplied: boolean) => void;
  setIsLoadingIcpData: (isLoading: boolean) => void;

  // ICP availability management
  markIcpAppliedToSam: () => void;
  markIcpAppliedToSom: () => void;
  clearIcpBannerFlags: () => void;

  // Helper methods
  fetchCompanyMatchData: (
    fileId: string
  ) => Promise<CompanyMatchUploadResponse | null>;

  // Workspace integration methods
  loadICPFromWorkspace: () => Promise<void>;
  saveICPToDraft: () => void;
  resetICPState: () => void;
}

export const createWorkspaceICPSlice: StateCreator<
  WorkspaceState & WorkspaceICPSlice,
  [],
  [],
  WorkspaceICPSlice
> = (set, get) => ({
  icpSelectedProducts: [],
  icpSAM: undefined,
  icpSOM: undefined,
  icpAnalysisMethod: ICPAnalysisMethod.PRODUCT_BASED,
  icpCustomerListData: undefined,
  isSamIcpAppliedToConfig: false,
  isSomIcpAppliedToConfig: false,
  isNewIcpAvailableForSam: false,
  isNewIcpAvailableForSom: false,
  isLoadingIcpData: false,

  // Distribution data initial state
  icpIndustryDistribution: [],
  icpGeographyDistribution: [],
  icpRevenueDistribution: [],
  icpEmployeeCountDistribution: [],

  setICPSelectedProducts: (productIds) =>
    set({ icpSelectedProducts: productIds }),
  setIcpSAM: (sam) =>
    set({
      icpSAM: sam,
      isNewIcpAvailableForSam: true,
    }),
  setIcpSOM: (som) =>
    set({
      icpSOM: som,
      isNewIcpAvailableForSom: true,
    }),
  setICPAnalysisMethod: (method) =>
    set(() => {
      // Clear data from the non-selected method when switching
      const updates: Partial<WorkspaceICPSlice> = { icpAnalysisMethod: method };

      if (method === ICPAnalysisMethod.PRODUCT_BASED) {
        updates.icpCustomerListData = undefined;
      } else if (method === ICPAnalysisMethod.CUSTOMER_LIST) {
        updates.icpSelectedProducts = [];
      }

      return updates;
    }),
  setICPCustomerListData: (data) => set({ icpCustomerListData: data }),

  // Distribution data setters
  setICPIndustryDistribution: (data) => set({ icpIndustryDistribution: data }),
  setICPGeographyDistribution: (data) =>
    set({ icpGeographyDistribution: data }),
  setICPRevenueDistribution: (data) => set({ icpRevenueDistribution: data }),
  setICPEmployeeCountDistribution: (data) =>
    set({ icpEmployeeCountDistribution: data }),

  setIsSamIcpAppliedToConfig: (isApplied) =>
    set({ isSamIcpAppliedToConfig: isApplied }),
  setIsSomIcpAppliedToConfig: (isApplied) =>
    set({ isSomIcpAppliedToConfig: isApplied }),
  setIsLoadingIcpData: (isLoading) => set({ isLoadingIcpData: isLoading }),

  resetICPState: () =>
    set({
      icpSelectedProducts: [],
      icpCustomerListData: undefined,
      icpAnalysisMethod: ICPAnalysisMethod.PRODUCT_BASED,
      icpSAM: undefined,
      icpSOM: undefined,
      isSamIcpAppliedToConfig: false,
      isSomIcpAppliedToConfig: false,
      isNewIcpAvailableForSam: false,
      isNewIcpAvailableForSom: false,
      isLoadingIcpData: false,
      // Reset distribution data as well
      icpIndustryDistribution: [],
      icpGeographyDistribution: [],
      icpRevenueDistribution: [],
      icpEmployeeCountDistribution: [],
    }),

  // Helper method to fetch complete company match data via GraphQL
  fetchCompanyMatchData: async (fileId: string) => {
    try {
      const client = GraphQLClient();
      const result = await client.query<CompanyMatchStatusPayload>({
        query: COMPANY_MATCH_FILE_STATS,
        variables: { fileId },
        fetchPolicy: "network-only", // Always fetch fresh data
        context: { clientName: COMPANY_MATCH_CLIENT },
      });

      return result.data?.stats || null;
    } catch (error) {
      console.error("Failed to fetch company match data:", error);
      return null;
    }
  },

  // Workspace integration methods
  loadICPFromWorkspace: async () => {
    const state = get();
    set({ isLoadingIcpData: true });

    try {
      if (state.draft?.configuration?.icp) {
        const icpConfig = state.draft.configuration.icp;

        let customerMatchData = icpConfig.customerMatch;

        // If we have a fileId but missing win/loss data, fetch it via GraphQL
        if (
          customerMatchData?.fileId &&
          (customerMatchData.salesWins === undefined ||
            customerMatchData.salesWins === null) &&
          (customerMatchData.salesLosses === undefined ||
            customerMatchData.salesLosses === null)
        ) {
          const completeData = await get().fetchCompanyMatchData(
            customerMatchData.fileId
          );

          if (completeData) {
            console.log("Retrieved complete data from GraphQL:", {
              salesWins: completeData.salesWins,
              salesLosses: completeData.salesLosses,
            });

            // Merge the complete data with existing data
            customerMatchData = {
              ...customerMatchData,
              salesWins: completeData.salesWins ?? null,
              salesLosses: completeData.salesLosses ?? null,
              matchedRows:
                completeData.matchedRows || customerMatchData.matchedRows,
              invalidRows:
                completeData.invalidRows || customerMatchData.invalidRows,
            };
          }
        }

        set({
          icpSelectedProducts: icpConfig.productIds || [],
          icpAnalysisMethod: icpConfig.customerMatch
            ? ICPAnalysisMethod.CUSTOMER_LIST
            : ICPAnalysisMethod.PRODUCT_BASED,
          icpCustomerListData: customerMatchData
            ? {
                // customerMatchData has more itens than needed so we can't use it here directly.
                companyMatch: {
                  fileId: customerMatchData.fileId,
                  fileName: customerMatchData.fileName,
                  uploaded: customerMatchData.uploaded,
                  lastMatched: customerMatchData.lastMatched,
                  totalRows: customerMatchData.totalRows,
                  processedRows: customerMatchData.processedRows,
                  matchedRows: customerMatchData.matchedRows,
                  invalidRows: customerMatchData.invalidRows,
                  salesWins: customerMatchData.salesWins,
                  salesLosses: customerMatchData.salesLosses,
                  processedChunks: customerMatchData.processedChunks,
                  totalChunks: customerMatchData.totalChunks,
                },
              }
            : undefined,
        });
      } else {
        get().resetICPState();
      }
    } finally {
      set({ isLoadingIcpData: false });
    }
  },

  saveICPToDraft: () => {
    const state = get();
    const { draft } = state;

    if (draft) {
      const customerMatch = state.icpCustomerListData?.companyMatch;
      const hasProducts =
        state.icpSelectedProducts && state.icpSelectedProducts.length > 0;
      const hasCustomerMatch = customerMatch && customerMatch.lastMatched;

      // Only create ICP config if there's meaningful data
      let icpConfig: ICPConfig | undefined = undefined;

      if (state.icpAnalysisMethod === ICPAnalysisMethod.CUSTOMER_LIST) {
        // For customer list method, only include config if there's a processed customer match
        if (hasCustomerMatch) {
          icpConfig = {
            productIds: [],
            customerMatch: {
              fileId: customerMatch.fileId,
              fileName: customerMatch.fileName || "",
              uploaded: customerMatch.uploaded || "",
              lastMatched: customerMatch.lastMatched || null,
              totalRows: customerMatch.totalRows || null,
              processedRows: customerMatch.processedRows || null,
              matchedRows: customerMatch.matchedRows || null,
              invalidRows: customerMatch.invalidRows || null,
              salesWins: customerMatch.salesWins || null,
              salesLosses: customerMatch.salesLosses || null,
              processedChunks: customerMatch.processedChunks || null,
              totalChunks: customerMatch.totalChunks || null,
            },
          };
        }
      } else {
        // For product-based method, only include config if there are selected products
        if (hasProducts) {
          icpConfig = {
            productIds: state.icpSelectedProducts,
            customerMatch: undefined,
          };
        }
      }

      const updatedDraft = {
        ...draft,
        configuration: {
          ...draft.configuration,
          icp: icpConfig,
        },
      };

      state.setDraft(updatedDraft);
    }
  },

  markIcpAppliedToSam: () =>
    set({
      isNewIcpAvailableForSam: false,
      isSamIcpAppliedToConfig: true,
    }),
  markIcpAppliedToSom: () =>
    set({
      isNewIcpAvailableForSom: false,
      isSomIcpAppliedToConfig: true,
    }),
  clearIcpBannerFlags: () =>
    set({
      isNewIcpAvailableForSam: false,
      isNewIcpAvailableForSom: false,
    }),
});
